// k:env-boundary — the config loader reads declared env vars at load time (BE-005).
import { readFileSync, statSync } from 'fs';
import { resolve, join } from 'path';
import { homedir } from 'os';
import { parse as parseYaml } from 'yaml';
import { shuttleConfigSchema, type ShuttleConfig } from './schema';
import { expandHome } from '../branding/apply';

export interface LoadConfigOptions {
  /** --config flag value, if the user provided one. */
  explicitPath?: string;
  /** Brand-supplied search paths; takes precedence over the default. */
  searchPaths?: ReadonlyArray<string>;
  /** Used to derive the substrate fallback path `~/.config/whittle/<id>.yaml`. */
  productId?: string;
}

function fileExists(filePath: string): boolean {
  try {
    statSync(filePath);
    return true;
  } catch {
    return false;
  }
}

function defaultSearchPaths(productId: string): string[] {
  return [
    resolve('shuttle.yaml'),
    resolve(`${productId}.yaml`),
    join(homedir(), '.config', 'whittle-labs', `${productId}.yaml`),
    join(homedir(), '.config', 'whittle-labs', 'shuttle.yaml'),
  ];
}

function resolveConfigPath(opts: LoadConfigOptions): string {
  if (opts.explicitPath) {
    const resolved = resolve(opts.explicitPath);
    if (!fileExists(resolved)) {
      throw new Error(`Config file not found: ${resolved}`);
    }
    return resolved;
  }

  const productId = opts.productId ?? 'shuttle';
  const candidates =
    opts.searchPaths && opts.searchPaths.length > 0
      ? opts.searchPaths.map((p) => expandHome(p))
      : defaultSearchPaths(productId);

  for (const candidate of candidates) {
    if (fileExists(candidate)) {
      return candidate;
    }
  }

  throw new Error(
    [
      'No shuttle.yaml config file found. Searched:',
      ...candidates.map((p) => `  - ${p}`),
      'Provide an explicit path with --config <path>',
    ].join('\n'),
  );
}

function interpolateEnvVars(value: unknown): unknown {
  if (typeof value === 'string') {
    return value.replace(/\$\{([^}]+)}/g, (_match, varName: string) => {
      const envValue = process.env[varName];
      if (envValue === undefined) {
        throw new Error(
          `Environment variable "${varName}" referenced in config is not set`,
        );
      }
      return envValue;
    });
  }

  if (Array.isArray(value)) {
    return value.map(interpolateEnvVars);
  }

  if (value !== null && typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      result[key] = interpolateEnvVars(val);
    }
    return result;
  }

  return value;
}

export function loadConfig(opts: LoadConfigOptions = {}): ShuttleConfig {
  const filePath = resolveConfigPath(opts);

  let raw: string;
  try {
    raw = readFileSync(filePath, 'utf-8');
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    throw new Error(`Failed to read config file ${filePath}: ${message}`);
  }

  let parsed: unknown;
  try {
    parsed = parseYaml(raw);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    throw new Error(`Failed to parse YAML in ${filePath}: ${message}`);
  }

  if (parsed === null || parsed === undefined || typeof parsed !== 'object') {
    throw new Error(`Config file ${filePath} is empty or not a valid YAML object`);
  }

  const interpolated = interpolateEnvVars(parsed);

  const result = shuttleConfigSchema.safeParse(interpolated);
  if (!result.success) {
    const issues = result.error.issues
      .map((issue) => `  - ${issue.path.join('.')}: ${issue.message}`)
      .join('\n');
    throw new Error(`Invalid config in ${filePath}:\n${issues}`);
  }

  return result.data;
}
